import {
  WSEmptyState,
  WSGrid,
  WSInfoBox,
  WSList,
  WSPage,
  WSPageToolbar,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  TaxFormStatus,
  TaxFormType
} from "@wingspanhq/payments/dist/interfaces/taxForm";
import React, { useMemo } from "react";
import { RouteComponentProps, useHistory } from "react-router-dom";
import { WSQueries } from "../../../query/WSQuery";
import { useQueryMemberPrivateFiles } from "../../../query/files/queries";
import { useUserId } from "../../../query/hooks/helpers";
import { useFeatureFlags } from "../../../query/hooks/useFeatureFlags";
import {
  AcknowledgementName,
  AcknowledgementStatus
} from "@wingspanhq/wingspan-api-sdk";
import { queriesAcknowledgements, queriesPayers } from "@query";
import { IPayerResponse } from "@wingspanhq/payments/dist/interfaces";
import { useQueryCustomerEntity } from "../../../query/onboarding/queries/useQueryCustomerEntity";
import { useQueryPayeeTaxForms } from "../../../query/taxForm/queries/useQueryPayeeTaxForms";
import {
  useQueryActivities,
  useQueryMemberProfile
} from "../../../query/users/queries";
import { DataView } from "../../../shared/components/DataView";
import { TextBulleted } from "../../../shared/components/TextBulleted";
import { useBrowserPageTitle } from "../../../shared/hooks/useBrowserPageTitle";
import { selectorAddressString } from "../../../shared/selectors/selectorAddressString";
import { OptOutPayerCard } from "../components/OptOutPayerCard";
import { SelectTaxYear } from "../components/SelectYear";
import { TaxDocumentsDetailsFAQ } from "../components/TaxDocumentsDetailsFAQ";
import { TaxFormCard } from "../components/TaxFormCard";
import { TaxFormT4ACard } from "../components/TaxFormT4ACard";
import { useUpdateDeliveryPreferencesModal } from "../components/useUpdateDeliveryPreferencesModal";
import { selectorT4ADocument } from "../selectors/selectorT4ADocument";
import { useCurrentTaxDocumentsYear } from "../utils/useCurrentTaxDocumentsYear";
import { PATH_SETTINGS_ACCOUNT_INFO } from "../../Settings/paths";
import { openIntercom } from "../../../shared/utils/intercom";
import { ConfirmTaxInformationForLegacyUserAlertWithClients } from "../components/ConfirmTaxInformationForLegacyUserAlertWithClients";

export const RouteIndex: React.FC<RouteComponentProps> = () => {
  const history = useHistory();
  const currentYear = useCurrentTaxDocumentsYear();

  const isAfterDeadline = useMemo(() => {
    const now = new Date();
    const filingDeadline = new Date(currentYear + 1, 0, 31, 23, 59, 59); // 31th of January

    return now > filingDeadline;
  }, [currentYear]);

  useBrowserPageTitle(`${currentYear} Tax Documents`);

  const userId = useUserId();
  const queryMember = useQueryMemberProfile(userId);
  const queryFeatureFlags = useFeatureFlags();
  const queryPayeeTaxForms = useQueryPayeeTaxForms({
    year: currentYear
  });
  const queryActivity = useQueryActivities(userId);

  const updateDeliveryPreferencesModal = useUpdateDeliveryPreferencesModal();
  const queryAllPayers = queriesPayers.listPayers.useQueryAll();

  const queryMonitoredEntity = useQueryCustomerEntity();
  const queryElectronicTaxFormConsentAcknowledgement =
    queriesAcknowledgements.getAcknowledgement.useQuery(
      {
        acknowledgementName: AcknowledgementName.ELECTRONIC_TAX_FORM_CONSENT
      },
      {
        enabled: !!queryMonitoredEntity.data
      }
    );

  const queryFiles = useQueryMemberPrivateFiles({
    filter: {
      hidden: true,
      tags: {
        in: [`payee:${userId}`]
      }
    },
    page: {
      size: 100
    }
  });

  const isUSTaxResident = useMemo(
    () =>
      !queryMonitoredEntity.data?.country ||
      queryMonitoredEntity.data?.country === "US",
    [queryMonitoredEntity.data?.country]
  );

  return (
    <WSPage>
      <WSPageToolbar title="Tax Documents" titleAfter={<SelectTaxYear />} />

      <WSQueries
        queries={{
          queryPayeeTaxForms,
          queryMember,
          queryFeatureFlags,
          queryActivity,
          queryAllPayers,
          queryFiles
        }}
      >
        {({
          queryPayeeTaxFormsData,
          queryMemberData,
          queryFeatureFlagsData,
          queryActivityData,
          queryFilesData
        }) => {
          const filteredTaxForms = queryPayeeTaxFormsData.filter(taxForm => {
            if (taxForm.status === TaxFormStatus.Pending) {
              return false;
            }

            if (taxForm.type === TaxFormType.T4ASlip) {
              const matchingFile = queryFilesData.find(file =>
                selectorT4ADocument(file, taxForm.clientId, currentYear)
              );

              return !!matchingFile;
            }

            return true;
          });

          // TODO (v3-migration): the 1099 opt-out reads payerSettings.taxFilingSettings.
          // Per the V3 design (PLA-3198 #5) PayerSettings is the payer account's own
          // config and is intentionally not exposed on the payee's Payer surface, so
          // this can't be derived from the Payer here — it needs re-sourcing from the
          // tax/settings domain, not a Payer field. Card omitted meanwhile.
          const optOutPayers: IPayerResponse[] = [];

          const isElectronicDelivery =
            queryElectronicTaxFormConsentAcknowledgement.data
              ?.acknowledgementStatus === AcknowledgementStatus.GIVEN ||
            queryActivityData.events.electronic1099Consent;

          const isTaxFormsAvailable =
            filteredTaxForms.length > 0 || optOutPayers.length > 0;

          return (
            <WSList gap="XL">
              <ConfirmTaxInformationForLegacyUserAlertWithClients
                currentYear={currentYear}
              />
              <WSGrid gutter="2XL">
                <WSGrid.Item span={{ m: "8" }}>
                  <WSList gap="2XL">
                    <WSInfoBox>
                      Ensure your personal or business information is up to date
                      for tax reporting purposes in{" "}
                      <a
                        target="_self"
                        onClick={() => history.push(PATH_SETTINGS_ACCOUNT_INFO)}
                      >
                        Account Settings
                      </a>
                      .
                    </WSInfoBox>
                    <DataView
                      data={[
                        {
                          fullWidth: true,
                          label: {
                            text: "Delivery preference",
                            action: isUSTaxResident
                              ? {
                                  label: "Edit",
                                  onClick: () => {
                                    updateDeliveryPreferencesModal.open({
                                      mailDelivery: !isElectronicDelivery,
                                      isMonitoredEntity:
                                        !!queryMonitoredEntity.data
                                    });
                                  }
                                }
                              : undefined,
                            actionPosition: "left"
                          },
                          children: (
                            <WSInfoBox theme="info" mt="M">
                              <WSText.ParagraphSm color="gray500">
                                Changes to your delivery preference apply to
                                current year forms and any past year reissues
                                after changes are saved.
                              </WSText.ParagraphSm>
                            </WSInfoBox>
                          ),
                          value: isElectronicDelivery
                            ? "Electronic delivery"
                            : "Mail"
                        },
                        ...(isElectronicDelivery
                          ? []
                          : [
                              {
                                label: {
                                  text: "Delivery address",
                                  tooltip: {
                                    title:
                                      "The delivery address is either your personal or business address provided in your account settings. If your address has changed, please update it in your Account Settings to ensure your information is accurate and up to date.",
                                    dark: true
                                  }
                                },
                                value: queryMemberData.profile?.address
                                  ? selectorAddressString(
                                      queryMemberData.profile?.address
                                    )
                                  : "No address provided"
                              }
                            ])
                      ]}
                    />

                    {isTaxFormsAvailable ? (
                      <>
                        {filteredTaxForms.length > 0
                          ? filteredTaxForms.map(taxForm =>
                              taxForm.type === TaxFormType.T4ASlip ? (
                                <TaxFormT4ACard
                                  key={taxForm.taxFormId}
                                  taxForm={taxForm}
                                  files={queryFilesData}
                                  year={currentYear}
                                />
                              ) : (
                                <TaxFormCard
                                  key={taxForm.taxFormId}
                                  taxForm={taxForm}
                                  member={queryMemberData}
                                  activity={queryActivityData}
                                />
                              )
                            )
                          : null}

                        {optOutPayers.length > 0
                          ? optOutPayers.map(payer => (
                              <OptOutPayerCard
                                key={payer.payerId}
                                payer={payer}
                                year={currentYear}
                              />
                            ))
                          : null}
                      </>
                    ) : isAfterDeadline ? (
                      <WSEmptyState
                        py="3XL"
                        hasBorder
                        orientation="vertical"
                        type="documents"
                        title="No tax documents for this year"
                        description={
                          <WSText.ParagraphSm align="left">
                            <WSList gap="L">
                              <WSText.ParagraphSm>
                                If you do not expect a Form 1099-NEC for{" "}
                                {currentYear}, then you’re all set.
                              </WSText.ParagraphSm>
                              <WSText.ParagraphSm>
                                If you were expecting a Form 1099-NEC for{" "}
                                {currentYear}, here are a few things you can do:
                              </WSText.ParagraphSm>
                              <WSList gap="S">
                                <WSText.ParagraphSm weight="medium">
                                  1. Check your sign-up or sign-in email
                                </WSText.ParagraphSm>
                                <TextBulleted color="gray600">
                                  <li>
                                    A Form 1099-NEC is issued to the email that
                                    the payer has on file. Confirm the email and
                                    try signing up or signing in again.
                                  </li>
                                </TextBulleted>
                              </WSList>
                              <WSList gap="S">
                                <WSText.ParagraphSm weight="medium">
                                  2. Need more help?
                                </WSText.ParagraphSm>
                                <TextBulleted color="gray600">
                                  <li>
                                    If you’ve confirmed your email and you’re
                                    still getting this message, please contact
                                    Wingspan support below and have the payer
                                    name(s) ready so we can assist you.
                                  </li>
                                </TextBulleted>
                              </WSList>
                            </WSList>
                          </WSText.ParagraphSm>
                        }
                        actions={{
                          buttons: [
                            {
                              label: "Contact support",
                              kind: "Secondary",
                              onClick: openIntercom
                            }
                          ]
                        }}
                      />
                    ) : (
                      <WSEmptyState
                        py="3XL"
                        hasBorder
                        orientation="vertical"
                        type="documents"
                        title="No tax documents yet for this year"
                        description="Tax documents will appear here once submitted. Please ensure your personal or business details are up to date in Account Settings. Note that the deadline for payers to send 1099 forms is February 1st."
                        actions={{
                          buttons: [
                            {
                              label: "View account settings",
                              kind: "Secondary",
                              onClick: async () => {
                                history.push(PATH_SETTINGS_ACCOUNT_INFO);
                              }
                            }
                          ]
                        }}
                      />
                    )}
                  </WSList>
                </WSGrid.Item>
                <WSGrid.Item span={{ m: "4" }}>
                  {isUSTaxResident && <TaxDocumentsDetailsFAQ mb="2XL" />}
                </WSGrid.Item>
              </WSGrid>
            </WSList>
          );
        }}
      </WSQueries>
    </WSPage>
  );
};
